import React, { useState, useEffect, useRef } from 'react';
import { Search, X, FileText, Briefcase, CheckSquare, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

/**
 * Highlights matching text segments within a string.
 */
const HighlightedText = ({ text, query }: { text: string; query: string }) => {
  if (!query || query.length < 2 || !text) return <>{text}</>;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-yellow-200 dark:bg-yellow-700/50 text-inherit rounded-sm px-0.5">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

interface UniversalSearchProps {
  searchInputRef?: React.RefObject<HTMLInputElement | null>;
}

const UniversalSearch = ({ searchInputRef }: UniversalSearchProps) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Record<string, unknown> | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const localRef = useRef<HTMLInputElement | null>(null);
  const inputRef = searchInputRef || localRef;
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  // Flatten results for keyboard navigation
  const flatResults = results ? [
    ...results.contracts.map(r => ({ ...r, category: 'contracts' })),
    ...results.projects.map(r => ({ ...r, category: 'projects' })),
    ...results.tasks.map(r => ({ ...r, category: 'tasks' })),
    ...results.employees.map(r => ({ ...r, category: 'employees' }))
  ] : [];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchTimer = setTimeout(async () => {
      if (query.length >= 2) {
        setLoading(true);
        try {
          const response = await api.get(`/search?q=${encodeURIComponent(query)}`);
          setResults(response.data);
          setIsOpen(true);
        } catch (error: unknown) {
          console.error('Search error:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setResults(null);
        setIsOpen(false);
      }
    }, 300); // Debounce

    return () => clearTimeout(searchTimer);
  }, [query]);

  const handleKeyDown = (e) => {
    if (!isOpen || flatResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      handleResultClick(flatResults[selectedIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  };

  const handleResultClick = (result) => {
    setIsOpen(false);
    setQuery('');
    
    // Navigate with state to trigger modal open
    switch (result.type) {
      case 'contract':
        navigate('/contracts', { state: { openModalId: result.id } });
        break;
      case 'project':
        navigate('/projects', { state: { openModalId: result.id } });
        break;
      case 'task':
        navigate('/tasks', { state: { openModalId: result.id } });
        break;
      case 'employee':
        navigate('/admin', { state: { tab: 'employees', openModalId: result.id } });
        break;
      default:
        break;
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'contract': return <FileText size={16} />;
      case 'project': return <Briefcase size={16} />;
      case 'task': return <CheckSquare size={16} />;
      case 'employee': return <Users size={16} />;
      default: return <Search size={16} />;
    }
  };

  const getCategoryLabel = (type) => {
    switch (type) {
      case 'contract': return 'Contracts';
      case 'project': return 'Projects';
      case 'task': return 'Tasks';
      case 'employee': return 'Employees';
      default: return '';
    }
  };

  const hasResults = results && (
    results.contracts.length > 0 ||
    results.projects.length > 0 ||
    results.tasks.length > 0 ||
    results.employees.length > 0
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <Search 
          size={18} 
          className="absolute left-3 top-1/2 -translate-y-1/2"
        />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search projects, tasks...  ( / )"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && results && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className="w-64 lg:w-80 pl-10 pr-8 py-2 rounded-lg border text-sm transition-all focus:outline-none focus:ring-2"
          style={{
            background: 'var(--bg-primary)',
            borderColor: 'var(--border-primary)',
            color: 'var(--text-primary)',
          }}
        />
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setResults(null);
              setIsOpen(false);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (
        <div 
          className="absolute top-full left-0 right-0 mt-2 rounded-lg border shadow-lg overflow-hidden z-50"
          style={{
            background: 'var(--bg-secondary)',
            borderColor: 'var(--border-primary)',
            maxHeight: '400px',
            overflowY: 'auto'
          }}
        >
          {loading ? (
            <div className="p-4 text-center text-wmt-secondary" >              Searching...
            </div>
          ) : hasResults ? (
            <div>
              {['contracts', 'projects', 'tasks', 'employees'].map(category => {
                const items = results[category];
                if (items.length === 0) return null;
                
                return (
                  <div key={category}>
                    <div 
                      className="px-3 py-2 text-xs font-semibold uppercase tracking-wider"
                      style={{ 
                        background: 'var(--bg-tertiary)', 
                        color: 'var(--text-tertiary)' 
                      }}
                    >
                      {getCategoryLabel(items[0]?.type)}
                    </div>
                    {items.map((result, idx) => {
                      const flatIndex = flatResults.findIndex(
                        r => r.id === result.id && r.type === result.type
                      );
                      const isSelected = flatIndex === selectedIndex;
                      
                      return (
                        <button
                          key={`${result.type}-${result.id}`}
                          onClick={() => handleResultClick(result)}
                          className="w-full px-3 py-2 flex items-center gap-3 transition-colors text-left"
                          style={{
                            background: isSelected ? 'var(--bg-tertiary)' : 'transparent',
                            color: 'var(--text-primary)'
                          }}
                          onMouseEnter={() => setSelectedIndex(flatIndex)}
                        >
                          <span className="text-alliance-navy" >                            {getIcon(result.type)}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate"><HighlightedText text={result.name} query={query} /></div>
                            <div 
                              className="text-xs truncate flex items-center gap-1"
                            >
                              {result.number && <span><HighlightedText text={result.number} query={query} /></span>}
                              {result.number && (result.subtitle || result.project_number) && <span> • </span>}
                              {result.project_number && result.type === 'task' && (
                                <span className="font-mono text-alliance-steel" >                                  Project #{result.project_number}
                                </span>
                              )}
                              {!result.project_number && result.subtitle && <span>{result.subtitle}</span>}
                            </div>
                          </div>
                          <span 
                            className="text-xs px-2 py-0.5 rounded"
                            style={{
                              background: result.status === 'Active' ? 'var(--alliance-sage-light)' : 'var(--bg-tertiary)',
                              color: result.status === 'Active' ? 'var(--alliance-sage)' : 'var(--text-secondary)'
                            }}
                          >
                            {result.status}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ) : query.length >= 2 ? (
            <div className="p-4 text-center text-wmt-secondary" >              No results found for "{query}"
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default UniversalSearch;
